import type { IngestEvent } from './types.js';
import { fmtMoney, mmdd } from './format.js';

/**
 * What an arrival of stats says on screen (owner ask 2026-09-21: "when stats
 * come in show a notification with the username and the provider and the
 * total amount"). Pure, so the wording and the decision to show at all are
 * tested without a browser.
 *
 * The rules:
 *  - a notification leads with WHOSE and WHAT CHANGED: the rep (the username)
 *    and the source's own name, then the change in USD. The batch total rides
 *    underneath as context. Leading with the batch total would read as new
 *    money on every hourly pull, because most pulls restate the same days;
 *  - a pull that moved no money is NOT shown. Sources are pulled hourly and
 *    usually repeat themselves; announcing every repeat would train everyone
 *    to ignore the corner of the screen. It is still recorded, so the log can
 *    tell "ran and nothing moved" from "did not run";
 *  - a fall is shown with its sign, never hidden: a provider restating a day
 *    downwards is exactly what someone should see;
 *  - a non-USD row with no rate yet counts 0 in the amount and is named, so a
 *    number that looks small says why (the dashboard's rule, R6/H8);
 *  - the relationship the buy side gives the feeds appears when a link says
 *    so, after the provider - it is not known for a feed nobody has matched.
 */
export interface ArrivalToast {
  id: string;
  /** who and where from: "Loki · Mobilynx". */
  title: string;
  /** the change, signed: "+$412.55". */
  amount: string;
  /** whether the change was up, down, or nothing (styling and the sign). */
  direction: 'up' | 'down';
  /** the batch and its window: "$1,284.22 over 7 days · 12 Sep → 18 Sep". */
  detail: string;
  /** the relationship(s) the buy side gives these feeds, when known. */
  relationship: string | null;
  /** an FX warning when rows were left out of the amount. */
  note: string | null;
  accountId: string;
}

/** The source as a person names it: its own label, else the provider slug. */
export function sourceTitle(e: Pick<IngestEvent, 'rep' | 'provider' | 'providerLabel'>): string {
  const where = e.providerLabel?.trim() || e.provider;
  const who = e.rep?.trim();
  // No rep on the source (an import that predates them): the provider alone,
  // never a dangling separator.
  return who ? `${who} · ${where}` : where;
}

export function describeArrival(e: IngestEvent): ArrivalToast {
  const up = e.deltaUsd >= 0;
  const days = e.days === 1 ? '1 day' : `${e.days} days`;
  const window =
    e.firstDay && e.lastDay
      ? e.firstDay === e.lastDay
        ? mmdd(e.firstDay)
        : `${mmdd(e.firstDay)} → ${mmdd(e.lastDay)}`
      : null;
  // The native total only when the batch is all one currency and not USD:
  // "€1,204.00" beside the USD figure answers "why is this not what the
  // provider's page says".
  const native =
    e.currency && e.currency !== 'USD' && e.payoutNative != null
      ? ` · ${e.payoutNative.toLocaleString('en-US', { style: 'currency', currency: e.currency, currencyDisplay: 'narrowSymbol' })} reported`
      : '';
  return {
    id: e.id,
    title: sourceTitle(e),
    amount: `${up ? '+' : '−'}${fmtMoney(Math.abs(e.deltaUsd))}`,
    direction: up ? 'up' : 'down',
    detail: `${fmtMoney(e.payoutUsd)} over ${days}${window ? ` · ${window}` : ''}${native}`,
    relationship: e.relationships.length ? e.relationships.join(', ') : null,
    note: e.fxMissing
      ? `${e.fxMissing} row${e.fxMissing === 1 ? '' : 's'} not counted — no USD rate yet`
      : null,
    accountId: e.accountId,
  };
}

/** Whether an arrival is worth the screen: it moved money. */
export function worthShowing(e: IngestEvent): boolean {
  return e.deltaUsd !== 0;
}

/** Ids compare as numbers, not strings: "10" is newer than "9". */
function newerThan(id: string, baseline: string | null): boolean {
  if (baseline == null) return true;
  try {
    return BigInt(id) > BigInt(baseline);
  } catch {
    return id !== baseline;
  }
}

/** At most this many on screen at once: a stack, not a wall. */
export const MAX_TOASTS = 4;

/**
 * The toasts to add for one answer: newer than the baseline, not already
 * shown, worth showing, oldest first (so the newest ends up on top of the
 * stack), capped.
 */
export function toastsFor(
  events: readonly IngestEvent[],
  baseline: string | null,
  shown: ReadonlySet<string>,
): ArrivalToast[] {
  return events
    .filter((e) => newerThan(e.id, baseline) && !shown.has(e.id) && worthShowing(e))
    .slice(0, MAX_TOASTS)
    .reverse()
    .map(describeArrival);
}

/** The newest id in an answer (they arrive newest first), else the baseline. */
export function newestId(events: readonly IngestEvent[], baseline: string | null): string | null {
  let out = baseline;
  for (const e of events) if (newerThan(e.id, out)) out = e.id;
  return out;
}
